/**
 * About page - Comprehensive redesign aligned with professional standards
 */

import React from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {StatCard} from '../../components/landing/StatCard';
import {CompanyLogoGrid} from '../../components/landing/CompanyLogoGrid';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

// Company stats
const companyStats = [
  {
    number: '500K+',
    description: 'Meetings transcribed',
  },
  {
    number: '10K+',
    description: 'Teams using VoxaPilot',
  },
  {
    number: '50M+',
    description: 'Minutes of meeting intelligence',
  },
];

// Company logos
const customerLogos = [
  {name: 'TechCorp', initials: 'TC'},
  {name: 'StartupXYZ', initials: 'SX'},
  {name: 'BigCo', initials: 'BC'},
  {name: 'InnovateLabs', initials: 'IL'},
  {name: 'DataFlow', initials: 'DF'},
  {name: 'CloudSync', initials: 'CS'},
  {name: 'ScaleUp', initials: 'SU'},
  {name: 'NextGen', initials: 'NG'},
  {name: 'FutureTech', initials: 'FT'},
];

// Values - removed emojis, using clean design
const values = [
  {
    title: 'Mission-Driven',
    description: 'We believe meetings should be productive, not painful. Every feature we build is designed to help teams make better decisions faster.',
  },
  {
    title: 'Innovation First',
    description: 'Constantly pushing the boundaries of AI and meeting technology. We invest heavily in research to stay ahead of the curve.',
  },
  {
    title: 'Customer-Centric',
    description: 'Your success is our success. We build with you, for you. Every decision we make starts with understanding your needs.',
  },
  {
    title: 'Privacy & Security',
    description: 'Your data is yours. We protect it with enterprise-grade security, zero data retention policies, and complete transparency.',
  },
];

// Company milestones
const milestones = [
  {
    year: '2023',
    title: 'Founded',
    description: 'VoxaPilot was born from a simple observation: teams waste hours every week in unproductive meetings.',
  },
  {
    year: '2024',
    title: 'First 1,000 Teams',
    description: 'Reached our first major milestone with teams across industries trusting VoxaPilot for their meeting intelligence.',
  },
  {
    year: '2025',
    title: 'Enterprise Launch',
    description: 'Launched enterprise features, serving Fortune 500 companies and scaling to handle millions of meeting minutes.',
  },
];

export function AboutPage() {
  const {ref: heroRef, isVisible: heroVisible} = useScrollAnimation();
  const {ref: missionRef, isVisible: missionVisible} = useScrollAnimation();
  const {ref: statsRef, isVisible: statsVisible} = useScrollAnimation();
  const {ref: storyRef, isVisible: storyVisible} = useScrollAnimation();
  const {ref: valuesRef, isVisible: valuesVisible} = useScrollAnimation();
  const {ref: milestonesRef, isVisible: milestonesVisible} = useScrollAnimation();
  const {ref: customersRef, isVisible: customersVisible} = useScrollAnimation();
  const {ref: ctaRef, isVisible: ctaVisible} = useScrollAnimation();

  return (
    <LandingLayout>
      {/* Hero Section */}
      <Section padding="lg" background="white" className="pt-32 pb-20 md:pb-24 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div
            ref={heroRef as React.RefObject<HTMLDivElement>}
            className={`max-w-4xl mx-auto text-center transition-all duration-700 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-textPrimary dark:text-[#CCCCCC] mb-6 leading-tight">
              Building the Future of
              <br />
              <span className="text-gray-600 dark:text-[#858585]">Meeting Intelligence</span>
            </h1>
            <p className="text-xl sm:text-2xl text-textSecondary dark:text-[#858585] max-w-3xl mx-auto leading-relaxed">
              We're on a mission to transform how teams collaborate, communicate, and make decisions through AI-powered meeting intelligence.
            </p>
          </div>
        </Container>
      </Section>

      {/* Mission Statement Section */}
      <Section padding="lg" background="gray" ref={missionRef as React.RefObject<HTMLElement>}>
        <Container>
          <div className={`max-w-4xl mx-auto transition-all duration-700 ${
            missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-8 md:p-12 border border-gray-200 dark:border-gray-700 shadow-lg">
              <div className="text-center mb-8">
                <p className="text-sm font-medium text-orange-600 dark:text-orange-500 mb-3 uppercase tracking-wide">
                  Our Mission
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-6">
                  Make Every Meeting Count
                </h2>
              </div>
              <div className="prose prose-lg max-w-none text-textSecondary dark:text-[#858585] leading-relaxed space-y-4">
                <p className="text-lg">
                  VoxaPilot was founded with a simple mission: make every meeting count. We saw teams drowning in meetings, struggling to keep up with notes, and missing critical insights that could drive better decisions.
                </p>
                <p>
                  Today, VoxaPilot helps thousands of teams transform their meetings with AI-powered transcription, intelligent summaries, and actionable insights. We're building the future of meeting intelligence—one where every conversation becomes a catalyst for progress.
                </p>
                <p>
                  Our platform doesn't just record meetings; it understands them. We extract key decisions, action items, and insights automatically, so teams can focus on what matters most: building, creating, and moving forward.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Stats Section */}
      <Section padding="lg" background="white" ref={statsRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`transition-all duration-700 ${
            statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className={`text-center mb-12 transition-all duration-700 ${
              statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
                Trusted by Teams Worldwide
              </h2>
              <p className="text-lg text-textSecondary dark:text-[#858585]">
                Join thousands of teams transforming their meeting culture
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {companyStats.map((stat, index) => (
                <div
                  key={stat.number}
                  className={`transition-all duration-700 ${
                    statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
                >
                  <StatCard number={stat.number} description={stat.description} />
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Story Section */}
      <Section padding="lg" background="gray" ref={storyRef as React.RefObject<HTMLElement>}>
        <Container>
          <div className={`max-w-4xl mx-auto transition-all duration-700 ${
            storyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Our Story"
              subtitle="From frustration to innovation"
              centered
            />
            <div className="prose prose-lg max-w-none text-textSecondary dark:text-[#858585] leading-relaxed space-y-6">
              <p>
                VoxaPilot started with a problem we all know too well: the meeting that should have been an email, the notes that never got written, and the decisions that got lost in translation.
              </p>
              <p>
                Our founders, having spent years in fast-paced tech environments, experienced firsthand how meeting inefficiency was slowing down entire organizations. They watched brilliant ideas get forgotten, action items slip through cracks, and teams repeat the same discussions week after week.
              </p>
              <p>
                So we built VoxaPilot—not just another meeting tool, but a complete meeting intelligence platform. We combined cutting-edge AI with thoughtful design to create something that actually makes meetings better, not just easier to record.
              </p>
              <p>
                Today, we're proud to serve teams from startups to Fortune 500 companies, helping them turn every meeting into actionable intelligence. But we're just getting started. The future of work is collaborative, and we're building the tools to make that collaboration more intelligent, more efficient, and more impactful.
              </p>
            </div>
          </div>
        </Container>
      </Section>

      {/* Values Section */}
      <Section padding="lg" background="white" ref={valuesRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`transition-all duration-700 ${
            valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Our Values"
              subtitle="The principles that guide everything we do"
              centered
            />
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {values.map((value, index) => (
                <div
                  key={value.title}
                  className={`transition-all duration-700 ${
                    valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
                >
                  <Card hover className="p-6 md:p-8 h-full">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC]">
                        {value.title}
                      </h3>
                      <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Milestones Timeline */}
      <Section padding="lg" background="gray" ref={milestonesRef as React.RefObject<HTMLElement>}>
        <Container>
          <div className={`max-w-4xl mx-auto transition-all duration-700 ${
            milestonesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Our Journey"
              subtitle="Key milestones in our growth"
              centered
            />
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                  <div
                    key={milestone.year}
                    className={`flex gap-6 md:gap-8 transition-all duration-700 ${
                      milestonesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                    }`}
                    style={{transitionDelay: `${0.2 + (index * 0.15)}s`}}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-white dark:bg-[#1E1E1E] border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center">
                        <span className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC]">{milestone.year}</span>
                      </div>
                    </div>
                    <div className="flex-1 pt-2">
                      <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Customer Logos */}
      <Section padding="lg" background="white" ref={customersRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`transition-all duration-700 ${
            customersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
                Trusted by Leading Companies
              </h2>
              <p className="text-lg text-textSecondary dark:text-[#858585]">
                Teams from startups to enterprises rely on VoxaPilot
              </p>
            </div>
            <CompanyLogoGrid companies={customerLogos} />
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section padding="lg" background="gray" ref={ctaRef as React.RefObject<HTMLElement>}>
        <Container>
          <div className={`max-w-3xl mx-auto text-center transition-all duration-700 ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Join Us on This Journey
            </h2>
            <p className="text-lg sm:text-xl text-textSecondary dark:text-[#858585] mb-10 max-w-2xl mx-auto leading-relaxed">
              We're always looking for talented people who share our passion for transforming how teams work. Check out our open positions or get in touch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTES.CAREERS}>
                <Button variant="primary" size="lg" className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white">
                  View Careers
                </Button>
              </Link>
              <Link to={ROUTES.CONTACT}>
                <Button variant="outline" size="lg">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}
